import { Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { ISTRES_MARKETS } from "@/lib/istres";
import {
  Calendar, MapPin, Clock, Store, ShoppingBasket, Tag, Search, ChevronLeft, ChevronRight, Car, Users,
} from "lucide-react";

const DAYS_SHORT = ["DIM", "LUN", "MAR", "MER", "JEU", "VEN", "SAM"];
const DAYS_FULL = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
const MONTHS = ["jan", "fév", "mars", "avr", "mai", "juin", "juil", "août", "sept", "oct", "nov", "déc"];
const MONTHS_FULL = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const DAY_INDEX = { dimanche: 0, lundi: 1, mardi: 2, mercredi: 3, jeudi: 4, vendredi: 5, samedi: 6 };

const FILTERS = [
  { id: "all", label: "Tous", emoji: "✦", color: "bg-plum-700 text-white" },
  { id: "market", label: "Marchés", emoji: "🧺", color: "bg-olive-500 text-white" },
  { id: "promo", label: "Promos", emoji: "%", color: "bg-pink-500 text-white" },
  { id: "atelier", label: "Ateliers", emoji: "✶", color: "bg-lavande-500 text-white" },
  { id: "fete", label: "Fêtes", emoji: "✷", color: "bg-sun-500 text-plum-900" },
];

const CAT_STYLE = {
  market: { dot: "bg-olive-500", chip: "bg-olive-50 text-olive-700 border-olive-200", label: "Marché" },
  promo: { dot: "bg-pink-500", chip: "bg-pink-50 text-pink-600 border-pink-200", label: "Promo" },
  ouverture: { dot: "bg-mint-500", chip: "bg-mint-50 text-mint-600 border-mint-200", label: "Ouverture" },
  fete: { dot: "bg-sun-500", chip: "bg-sun-50 text-sun-700 border-sun-200", label: "Fête" },
  atelier: { dot: "bg-lavande-500", chip: "bg-lavande-50 text-lavande-700 border-lavande-200", label: "Atelier" },
  evenement: { dot: "bg-plum-500", chip: "bg-cream-100 text-plum-700 border-cream-200", label: "Événement" },
};

function dKey(d) { return d.toISOString().slice(0, 10); }
function sameDay(a, b) { return a.toDateString() === b.toDateString(); }

function expandMarketDates(market, fromDate, weeks = 8) {
  const out = [];
  const dayRaw = (market.day || "").toLowerCase();
  const start = new Date(fromDate); start.setHours(0, 0, 0, 0);
  if (dayRaw.includes("du mois")) {
    const m = dayRaw.match(/(dimanche|lundi|mardi|mercredi|jeudi|vendredi|samedi)/);
    if (!m) return out;
    const tDow = DAY_INDEX[m[1]];
    for (let mo = 0; mo < 3; mo++) {
      const mStart = new Date(start.getFullYear(), start.getMonth() + mo, 1);
      const diff = (tDow - mStart.getDay() + 7) % 7;
      const d = new Date(mStart); d.setDate(1 + diff);
      if (d >= start) out.push(new Date(d));
    }
    return out;
  }
  const targets = Object.keys(DAY_INDEX).filter((d) => dayRaw.includes(d));
  for (let w = 0; w < weeks; w++) {
    for (const d of targets) {
      const t = DAY_INDEX[d];
      const cur = new Date(start);
      const off = (t - cur.getDay() + 7) % 7;
      cur.setDate(cur.getDate() + off + w * 7);
      if (cur >= start) out.push(new Date(cur));
    }
  }
  return out;
}

export default function AgendaTimeline() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const stripRef = useRef(null);

  useEffect(() => {
    api.get("/public/events-with-carpool")
      .then((r) => setEvents(r.data.items || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  // Build all items
  const allItems = useMemo(() => {
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const list = [];
    ISTRES_MARKETS.forEach((m) => {
      expandMarketDates(m, now, 8).forEach((d) => list.push({
        kind: "market", id: `${m.id}-${dKey(d)}`, date: d,
        title: m.name, subtitle: m.desc, when: m.hours, where: m.where,
        category: "market", icon: ShoppingBasket,
      }));
    });
    events.forEach((e) => {
      if (!e.start_at) return;
      const d = new Date(e.start_at); d.setHours(0, 0, 0, 0);
      if (isNaN(d.getTime()) || d < now) return;
      list.push({
        kind: "event", id: e.id, date: d,
        title: e.title, subtitle: e.merchant_name,
        when: e.end_at && e.end_at !== e.start_at ? `jusqu'au ${e.end_at}` : null,
        where: e.location, category: e.category || "evenement",
        merchant_logo: e.merchant_logo, icon: e.category === "promo" ? Tag : Store,
        carpool_driver_count: e.carpool_driver_count || 0,
        carpool_seats_remaining: e.carpool_seats_remaining || 0,
      });
    });
    return list.sort((a, b) => a.date - b.date);
  }, [events]);

  // 14 next days for the strip with counters
  const strip = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const days = [];
    for (let i = 0; i < 14; i++) {
      const d = new Date(today); d.setDate(today.getDate() + i);
      const count = allItems.filter((it) => sameDay(it.date, d) && (filter === "all" || it.category === filter)).length;
      days.push({ date: d, count });
    }
    return days;
  }, [allItems, filter]);

  // When a category filter is active we show ALL upcoming items of that
  // category (across all days). When "all" is selected we keep the per-day
  // view based on the strip.
  const isCategoryView = filter !== "all";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allItems.filter((it) => {
      if (!isCategoryView && !sameDay(it.date, selectedDate)) return false;
      if (isCategoryView && it.category !== filter) return false;
      if (q) {
        const hay = `${it.title} ${it.subtitle} ${it.where}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allItems, selectedDate, filter, query, isCategoryView]);

  // Group filtered items by day key for the category view
  const groupedByDay = useMemo(() => {
    if (!isCategoryView) return [];
    const groups = new Map();
    filtered.forEach((it) => {
      const key = dKey(it.date);
      if (!groups.has(key)) groups.set(key, { date: it.date, items: [] });
      groups.get(key).items.push(it);
    });
    return Array.from(groups.values()).sort((a, b) => a.date - b.date);
  }, [filtered, isCategoryView]);

  // Counters per filter
  const filterCounts = useMemo(() => {
    const m = { all: 0, market: 0, promo: 0, atelier: 0, fete: 0 };
    allItems.forEach((it) => {
      m.all++;
      if (m[it.category] !== undefined) m[it.category]++;
    });
    return m;
  }, [allItems]);

  const scrollStrip = (dir) => {
    if (!stripRef.current) return;
    stripRef.current.scrollBy({ left: dir * 220, behavior: "smooth" });
  };

  const isToday = (d) => sameDay(d, new Date());
  const isTomorrow = (d) => { const t = new Date(); t.setDate(t.getDate() + 1); return sameDay(d, t); };

  return (
    <section id="marches" className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-20" data-testid="agenda-timeline">
      <div className="mb-7 sm:mb-9 max-w-2xl">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sun-100 text-sun-700 mb-3 text-[12px] font-bold uppercase tracking-wider">
          <Calendar size={12} strokeWidth={2.5} /> Agenda d&apos;Istres
        </div>
        <h2 className="heading-display text-[1.85rem] sm:text-[2.6rem] text-plum-900 leading-[1.05]">
          Marchés &amp; événements
        </h2>
        <p className="mt-2 text-[15px] sm:text-[16px] text-plum-700/75">
          Choisissez un jour pour voir ce qui se passe à Istres.
        </p>
      </div>

      {/* === Search + Category filters === */}
      <div className="space-y-3 mb-5" data-testid="agenda-controls">
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-plum-700/40" strokeWidth={2.2} />
          <input
            data-testid="agenda-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un événement, un marché, une boutique..."
            className="w-full h-12 pl-11 pr-4 rounded-2xl bg-white border border-cream-200 text-[15px] placeholder:text-plum-700/40 focus:outline-none focus:ring-2 focus:ring-plum-500/30 focus:border-plum-300"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }}>
          {FILTERS.map((f) => {
            const active = filter === f.id;
            const count = filterCounts[f.id] ?? 0;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`agenda-filter-${f.id}`}
                className={`shrink-0 h-10 px-4 rounded-full text-[13px] font-bold transition-all flex items-center gap-1.5 ${
                  active ? f.color + " shadow-md" : "bg-white text-plum-700 border border-cream-200 hover:bg-cream-50"
                }`}
              >
                <span aria-hidden="true">{f.emoji}</span> {f.label}
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] tabular-nums ${active ? "bg-black/15" : "bg-cream-100 text-plum-700/70"}`}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* === Horizontal day picker (14 days) — hidden when filtering a category === */}
      {!isCategoryView && (
      <div className="relative mb-7" data-testid="agenda-day-strip">
        <button
          onClick={() => scrollStrip(-1)}
          className="hidden sm:grid absolute -left-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white shadow-md border border-cream-200 place-items-center text-plum-700 hover:bg-cream-50"
          aria-label="Précédent"
        >
          <ChevronLeft size={16} />
        </button>
        <div
          ref={stripRef}
          className="flex gap-2 overflow-x-auto pb-2 px-1 sm:px-8 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none" }}
        >
          {strip.map(({ date, count }) => {
            const active = sameDay(date, selectedDate);
            const today = isToday(date);
            const tomorrow = isTomorrow(date);
            return (
              <button
                key={dKey(date)}
                onClick={() => setSelectedDate(date)}
                data-testid={`agenda-day-${dKey(date)}`}
                className={`snap-start shrink-0 w-[68px] sm:w-[76px] h-[92px] rounded-2xl flex flex-col items-center justify-center transition-all relative ${
                  active
                    ? "bg-plum-700 text-white shadow-lg shadow-plum-700/30 -translate-y-0.5"
                    : today
                    ? "bg-pink-100 text-pink-700 border-2 border-pink-300"
                    : tomorrow
                    ? "bg-sun-100 text-sun-700 border-2 border-sun-300"
                    : "bg-white text-plum-900 border border-cream-200 hover:bg-cream-50"
                }`}
              >
                <div className={`text-[10px] font-bold uppercase tracking-wider mb-0.5 ${active ? "opacity-80" : "opacity-70"}`}>
                  {today ? "Auj." : tomorrow ? "Dem." : DAYS_SHORT[date.getDay()]}
                </div>
                <div className="heading-display text-2xl tabular-nums leading-none">{date.getDate()}</div>
                <div className={`text-[10px] font-semibold uppercase tracking-wider mt-0.5 ${active ? "opacity-75" : "opacity-55"}`}>
                  {MONTHS[date.getMonth()]}
                </div>
                {count > 0 && (
                  <span className={`absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] rounded-full text-[10px] font-bold grid place-items-center px-1 ${
                    active ? "bg-sun-300 text-plum-900" : "bg-plum-700 text-white"
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <button
          onClick={() => scrollStrip(1)}
          className="hidden sm:grid absolute -right-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white shadow-md border border-cream-200 place-items-center text-plum-700 hover:bg-cream-50"
          aria-label="Suivant"
        >
          <ChevronRight size={16} />
        </button>
      </div>
      )}

      {/* === Selected day header — only in "Tous" view === */}
      {!isCategoryView && (
      <div className="flex items-end justify-between mb-4">
        <div>
          <h3 className="heading-display text-[1.4rem] sm:text-[1.6rem] text-plum-900 leading-tight">
            {isToday(selectedDate) ? "Aujourd'hui" : isTomorrow(selectedDate) ? "Demain" : `${DAYS_FULL[selectedDate.getDay()]} ${selectedDate.getDate()} ${MONTHS_FULL[selectedDate.getMonth()]}`}
          </h3>
          <div className="text-[12px] text-plum-700/55 font-semibold uppercase tracking-wider mt-0.5">
            {filtered.length} événement{filtered.length > 1 ? "s" : ""} {filter !== "all" && `· filtre ${FILTERS.find((f) => f.id === filter)?.label}`}
          </div>
        </div>
      </div>
      )}

      {/* === Category overview header (e.g. "Marchés à venir") === */}
      {isCategoryView && (
        <div className="flex items-end justify-between mb-4">
          <div>
            <h3 className="heading-display text-[1.4rem] sm:text-[1.6rem] text-plum-900 leading-tight">
              {FILTERS.find((f) => f.id === filter)?.label} à venir
            </h3>
            <div className="text-[12px] text-plum-700/55 font-semibold uppercase tracking-wider mt-0.5">
              {filtered.length} prochain{filtered.length > 1 ? "s" : ""} sur les 8 semaines
            </div>
          </div>
        </div>
      )}

      {/* === Events list for selected day === */}
      {loading && (
        <div className="space-y-2">
          {[0, 1].map((i) => <div key={i} className="phenix-card p-4 h-20 animate-pulse" />)}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="phenix-card p-10 text-center bg-cream-50/40">
          <Calendar size={32} className="mx-auto text-plum-700/30 mb-3" />
          <h4 className="font-display font-bold text-plum-900 mb-1">
            {isCategoryView ? "Aucun événement à venir" : "Rien de prévu ce jour-là"}
          </h4>
          <p className="text-[13px] text-plum-700/60">
            {isCategoryView ? "Revenez bientôt — les commerçants ajoutent régulièrement de nouveaux événements." : "Essayez un autre jour ou retirez les filtres."}
          </p>
        </div>
      )}

      {!loading && filtered.length > 0 && !isCategoryView && (
        <ul className="space-y-2.5" data-testid="agenda-day-events">
          {filtered.map((it) => <AgendaItem key={it.id} it={it} />)}
        </ul>
      )}

      {!loading && filtered.length > 0 && isCategoryView && (
        <div className="space-y-7" data-testid="agenda-category-events">
          {groupedByDay.map((g) => (
            <div key={dKey(g.date)}>
              <div className="flex items-baseline gap-3 mb-2.5">
                <h4 className="font-display font-bold text-[15px] text-plum-900">
                  {isToday(g.date)
                    ? "Aujourd'hui"
                    : isTomorrow(g.date)
                    ? "Demain"
                    : `${DAYS_FULL[g.date.getDay()]} ${g.date.getDate()} ${MONTHS_FULL[g.date.getMonth()]}`}
                </h4>
                <span className="text-[11px] text-plum-700/55 font-semibold uppercase tracking-wider">
                  {g.items.length} événement{g.items.length > 1 ? "s" : ""}
                </span>
              </div>
              <ul className="space-y-2.5">
                {g.items.map((it) => <AgendaItem key={it.id} it={it} />)}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function AgendaItem({ it }) {
  const s = CAT_STYLE[it.category] || CAT_STYLE.evenement;
  const hasCarpool = (it.carpool_driver_count || 0) > 0;
  return (
    <li className="list-none">
      <article className="phenix-card phenix-card-hover p-4 flex gap-3 items-start" data-testid={`agenda-item-${it.id}`}>
        {it.merchant_logo ? (
          <img src={it.merchant_logo} alt="" className="w-11 h-11 rounded-full object-cover shrink-0 border-2 border-cream-100" />
        ) : (
          <div className={`w-11 h-11 rounded-full ${s.dot} text-white grid place-items-center shrink-0`}>
            <it.icon size={18} strokeWidth={2.2} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-0.5">
            <h4 className="font-display font-bold text-[15px] text-plum-900 leading-tight line-clamp-1">{it.title}</h4>
            <span className={`shrink-0 phenix-pill ${s.chip} border text-[10px]`}>{s.label}</span>
          </div>
          {it.subtitle && <p className="text-[12px] text-plum-700/65 line-clamp-1 mb-1.5">{it.subtitle}</p>}
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-plum-700/65">
            {it.when && (
              <span className="inline-flex items-center gap-1"><Clock size={11} strokeWidth={2.2} className="text-plum-500" /> {it.when}</span>
            )}
            {it.where && (
              <span className="inline-flex items-center gap-1"><MapPin size={11} strokeWidth={2.2} className="text-plum-500" /> {it.where}</span>
            )}
          </div>
          {hasCarpool && it.kind === "event" && (
            <Link
              to={`/covoiturage?event=${it.id}`}
              className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-mint-100 hover:bg-mint-200 text-mint-700 text-[11px] font-bold transition-colors"
              data-testid={`agenda-carpool-${it.id}`}
            >
              <Car size={11} strokeWidth={2.4} />
              {it.carpool_driver_count} chauffeur{it.carpool_driver_count > 1 ? "s" : ""}
              <span className="text-mint-600/70">·</span>
              <Users size={11} strokeWidth={2.4} />
              {it.carpool_seats_remaining} place{it.carpool_seats_remaining > 1 ? "s" : ""}
            </Link>
          )}
        </div>
      </article>
    </li>
  );
}
